import React, { useEffect, useMemo, useState } from "react";
import { apiGet } from "../Utils/apiFetch";
import apiPath from "../Utils/apiPath";
import { debounce, startCase } from "lodash";
import obj from "../Utils/helpers";
import moment from "moment";

const TABS = [
  { key: "cricket", label: "Cricket"     },
  { key: "soccer",  label: "Soccer"      },
  { key: "tennis",  label: "Tennis"      },
  { key: "virtual", label: "Virtual T10" },
];

const RESULT_TYPES = [
  { key: "matchOdds", label: "Match Odds"  },
  { key: "fancy",     label: "Fancy"       },
  { key: "premium",   label: "Sports Book" },
];

/* ── inline styles (no extra CSS file needed) ── */
const S = {
  overlay: {
    position: "fixed", inset: 0,
    background: "rgba(0,0,0,0.75)",
    zIndex: 9999,
    display: "flex", alignItems: "flex-end",
  },
  sheet: {
    background: "#16213e",
    width: "100%",
    maxHeight: "85vh",
    borderRadius: "16px 16px 0 0",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",          /* sheet itself doesn't scroll */
  },
  sheetHeader: {
    padding: "14px 16px 0",
    flexShrink: 0,
  },
  sheetBody: {
    flex: 1,
    overflowY: "auto",           /* vertical scroll inside sheet */
    padding: "0 16px 20px",
  },
  tabRow: {
    display: "flex", gap: "8px",
    padding: "10px 0",
    flexShrink: 0,
    overflowX: "auto",           /* tabs scroll if screen is narrow */
  },
  tab: (active) => ({
    padding: "6px 16px",
    borderRadius: "20px",
    border: "none",
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: "bold",
    whiteSpace: "nowrap",
    background: active ? "#184438" : "#2a2a3e",
    color:      active ? "#fff"    : "#aaa",
  }),
  tableWrap: {
    overflowX: "auto",           /* ← this is what was missing */
    WebkitOverflowScrolling: "touch",
  },
  table: {
    width: "100%",
    minWidth: "420px",           /* force scroll when narrow */
    borderCollapse: "collapse",
    fontSize: "13px",
    color: "#fff",
  },
  th: {
    background: "#0f3460",
    padding: "8px 10px",
    textAlign: "left",
    whiteSpace: "nowrap",
    borderBottom: "1px solid #2a2a3e",
  },
  td: {
    padding: "8px 10px",
    borderBottom: "1px solid #1e2a45",
    verticalAlign: "middle",
  },
  tdCenter: {
    padding: "8px 10px",
    borderBottom: "1px solid #1e2a45",
    textAlign: "center",
    verticalAlign: "middle",
  },
  gold: { color: "#184438", fontWeight: "bold" },
  empty: { color: "#888", textAlign: "center", padding: "24px 0", fontSize: "13px" },
  viewBtn: {
    padding: "3px 10px",
    fontSize: "11px",
    background: "#184438",
    color: "#fff",
    border: "1px solid #184438",
    borderRadius: "4px",
    cursor: "pointer",
    fontWeight: "bold",
  },
};

const ResultNew = () => {
  const [activeTab, setActiveTab] = useState("cricket");
  const [list,      setList]      = useState([]);
  const [loading,   setLoading]   = useState(false);

  const previousDate = moment().subtract(1, "days").format("YYYY-MM-DD");
  const currentDate  = moment().format("YYYY-MM-DD");

  const [filter, setFilter] = useState({
    fromPeriod: previousDate,
    toPeriod:   currentDate,
    keyword:    "",
    page:       1,
    limit:      50,
  });

  /* ── modal state ── */
  const [selectedMatch,   setSelectedMatch]   = useState(null);
  const [resultTab,       setResultTab]       = useState("matchOdds");
  const [fancyResult,     setFancyResult]     = useState([]);
  const [premiumResult,   setPremiumResult]   = useState([]);
  const [matchOddsResult, setMatchOddsResult] = useState(null);
  const [detailLoading,   setDetailLoading]   = useState(false);

  /* ── debounce search ── */
  const debounceFn = useMemo(
    () => debounce((val) => setFilter((p) => ({ ...p, keyword: val, page: 1 })), 800),
    []
  );

  /* ── fetch match list ── */
  const fetchMatchList = async (gameType) => {
    setLoading(true);
    try {
      const { status, data } = await apiGet(apiPath.resultList, {
        ...filter,
        gameType: gameType || activeTab,
      });
      if (status === 200 && data?.success) {
        setList(data?.results || data || []);
      }
    } catch (e) {
      console.error("fetchMatchList", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchMatchList(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchMatchList(); }, [filter.toPeriod, filter.keyword, filter.page, filter.fromPeriod]);

  /* ── fetch result detail for a match ── */
  const fetchMatchResults = async (match) => {
    setSelectedMatch(match);
    setResultTab("matchOdds");
    setFancyResult([]);
    setPremiumResult([]);
    setMatchOddsResult(null);
    setDetailLoading(true);

    try {
      const [fancyRes, premiumRes, matchOddsRes] = await Promise.allSettled([
        apiGet(apiPath.getFancyResult,     { eventId: match.eventId }),
        apiGet(apiPath.getPremiumResult,   { eventId: match.eventId }),
        apiGet(apiPath.getMatchOddsResult, { eventId: match.eventId, marketId: match.marketId }),
      ]);

      if (fancyRes.status === "fulfilled" && fancyRes.value?.status === 200)
        setFancyResult(fancyRes.value.data?.data || []);

      if (premiumRes.status === "fulfilled" && premiumRes.value?.status === 200)
        setPremiumResult(premiumRes.value.data?.data || []);

      if (matchOddsRes.status === "fulfilled" && matchOddsRes.value?.status === 200)
        setMatchOddsResult(matchOddsRes.value.data?.data || null);

    } catch (e) {
      console.error("fetchMatchResults", e);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedMatch(null);
    setFancyResult([]);
    setPremiumResult([]);
    setMatchOddsResult(null);
  };

  const getWinnerName = (res) => {
    if (!res?.runners) return res?.winner || "-";
    const runner = res.runners.find(
      (r) => String(r?.SelectionId || r?.selectionId) === String(res?.winner)
    );
    return runner?.RunnerName || runner?.runnerName || res?.winner || "-";
  };

  const matchList = Array.isArray(list?.data)
    ? list.data
    : Array.isArray(list)
    ? list
    : [];

  /* ═══════════════════════════════ RENDER ══════════════════════════════ */
  return (
    <div>
      <p className="reulst-h3">Check Result</p>

      {/* ── date filters ── */}
      <div className="resul-filter">
        <div className="result-time">
          <input
            type="date"
            value={filter.fromPeriod}
            max={new Date().toISOString().split("T")[0]}
            onChange={(e) => setFilter({ ...filter, fromPeriod: e.target.value, page: 1 })}
          />
          <div>
            <span style={{ marginRight: "5px", fontSize: "10px" }}>to</span>
            <input
              type="date"
              value={filter.toPeriod}
              disabled={!filter.fromPeriod}
              min={filter.fromPeriod
                ? new Date(filter.fromPeriod).toISOString().split("T")[0]
                : undefined}
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setFilter({ ...filter, toPeriod: e.target.value, page: 1 })}
            />
          </div>
        </div>

        {/* ── sport tabs ── */}
        <div style={{ padding: "7px 7px 4px", display: "flex", flexWrap: "wrap", gap: "4px" }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              className={`btn-result${activeTab === t.key ? " active" : ""}`}
              onClick={() => { setActiveTab(t.key); fetchMatchList(t.key); }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── search ── */}
      <div className="result-search">
        <input
          placeholder="Search..."
          onChange={(e) => debounceFn(e.target.value)}
          type="text"
        />
      </div>

      {/* ── match list ── */}
      <div style={{ marginBottom: "70px", width: "100%", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        {loading ? (
          <p style={{ textAlign: "center", padding: "20px" }}>Loading...</p>
        ) : (
          <table className="result-table" style={{ minWidth: "560px" }}>
            <thead>
              <tr>
                <th style={{ width: "6%",  textAlign: "center" }}>Sr</th>
                <th style={{ width: "10%", textAlign: "start"  }}>Sport</th>
                <th style={{ width: "17%", textAlign: "start"  }}>Date</th>
                <th style={{ width: "42%", textAlign: "start"  }}>Match Name</th>
                <th style={{ width: "15%", textAlign: "start"  }}>Winner</th>
                <th style={{ width: "10%", textAlign: "center" }}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {matchList.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: "24px" }}>
                    No results found
                  </td>
                </tr>
              ) : (
                matchList.map((res, index) => (
                  <tr key={res?.eventId || index}>
                    <td style={{ textAlign: "center" }}>{index + 1}</td>
                    <td>{startCase(res?.gameType || res?.eventType || "")}</td>
                    <td>{obj.dateFormat(res?.eventDateTime)}</td>
                    <td style={{ wordBreak: "break-word" }}>
                      {`${res?.eventName} (${res?.eventId})`}
                    </td>
                    <td style={{ color: "#184438", fontWeight: "bold" }}>
                      {getWinnerName(res)}
                    </td>
                    <td style={{ textAlign: "center" }}>
                      <button style={S.viewBtn} onClick={() => fetchMatchResults(res)}>
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {matchList.length > 0 && list?.hasNextPage && (
          <div className="load-more">
            <button
              className="btn-result"
              style={{ width: "150px" }}
              onClick={() => setFilter((p) => ({ ...p, page: list?.nextPage }))}
            >
              Load More
            </button>
          </div>
        )}
      </div>

      {/* ══════════════ RESULT DETAIL BOTTOM SHEET ══════════════ */}
      {selectedMatch && (
        <div
          style={S.overlay}
          onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
        >
          <div style={S.sheet}>

            {/* ── sheet header (fixed, doesn't scroll) ── */}
            <div style={S.sheetHeader}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <p style={{ color: "#fff", fontWeight: "bold", fontSize: "14px", margin: 0 }}>
                    {selectedMatch?.eventName}
                  </p>
                  <p style={{ color: "#888", fontSize: "11px", margin: "2px 0 0" }}>
                    Event ID: {selectedMatch?.eventId}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  style={{ background: "none", border: "none", color: "#fff", fontSize: "22px", cursor: "pointer", lineHeight: 1 }}
                >
                  ✕
                </button>
              </div>

              {/* result type tabs — scrollable row */}
              <div style={S.tabRow}>
                {RESULT_TYPES.map((t) => (
                  <button
                    key={t.key}
                    style={S.tab(resultTab === t.key)}
                    onClick={() => setResultTab(t.key)}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* thin divider */}
              <div style={{ height: "1px", background: "#2a2a3e", margin: "0 -16px" }} />
            </div>

            {/* ── sheet body (scrollable) ── */}
            <div style={S.sheetBody}>
              {detailLoading ? (
                <p style={{ color: "#aaa", textAlign: "center", padding: "32px 0" }}>Loading...</p>
              ) : (
                <>
                  {/* ── Match Odds ── */}
                  {resultTab === "matchOdds" && (
                    matchOddsResult ? (
                      <div style={S.tableWrap}>
                        <table style={S.table}>
                          <thead>
                            <tr>
                              <th style={S.th}>Market</th>
                              <th style={S.th}>Winner</th>
                              <th style={{ ...S.th, textAlign: "center" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td style={S.td}>Match Odds</td>
                              <td style={{ ...S.td, ...S.gold }}>
                                {matchOddsResult?.isRefund
                                  ? "Refund / Void"
                                  : matchOddsResult?.winnerName || matchOddsResult?.result || "-"}
                              </td>
                              <td style={S.tdCenter}>{matchOddsResult?.status || "-"}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={S.empty}>No Match Odds result available</p>
                    )
                  )}

                  {/* ── Fancy ── */}
                  {resultTab === "fancy" && (
                    fancyResult.length > 0 ? (
                      <div style={S.tableWrap}>
                        <table style={S.table}>
                          <thead>
                            <tr>
                              <th style={{ ...S.th, minWidth: "180px" }}>Fancy Name</th>
                              <th style={{ ...S.th, textAlign: "center", minWidth: "100px" }}>Result (Runs)</th>
                              <th style={{ ...S.th, textAlign: "center", minWidth: "80px" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {fancyResult.map((f, i) => (
                              <tr key={f?.selectionId || i}>
                                <td style={S.td}>{f?.fancyName || "-"}</td>
                                <td style={{ ...S.tdCenter, ...S.gold }}>{f?.decisionRun ?? "-"}</td>
                                <td style={S.tdCenter}>{f?.status || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={S.empty}>No Fancy result available for this match</p>
                    )
                  )}

                  {/* ── Sports Book ── */}
                  {resultTab === "premium" && (
                    premiumResult.length > 0 ? (
                      <div style={S.tableWrap}>
                        <table style={S.table}>
                          <thead>
                            <tr>
                              <th style={{ ...S.th, minWidth: "180px" }}>Market</th>
                              <th style={{ ...S.th, minWidth: "150px" }}>Winner</th>
                              <th style={{ ...S.th, textAlign: "center", minWidth: "80px" }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {premiumResult.map((p, i) => (
                              <tr key={p?.selectionId || i}>
                                <td style={S.td}>{p?.fancyName || "-"}</td>
                                <td style={{ ...S.td, ...S.gold }}>{p?.teamSelectionWin || "-"}</td>
                                <td style={S.tdCenter}>{p?.status || "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p style={S.empty}>No Sports Book result available for this match</p>
                    )
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResultNew;
